import { useState, useMemo, useCallback } from 'react';
import { DndContext, DragStartEvent, DragEndEvent, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CandidateStatus, CandidateSummary, CandidateStatusCounts } from '@recruitai/shared';
import { toast } from 'sonner';
import { KanbanColumn } from './KanbanColumn';
import { CandidateCard } from './CandidateCard';
import { KANBAN_COLUMNS, groupCandidatesByStatus, getValidTargets, getNextStatus, canReject } from './kanban-utils';

export interface KanbanBoardProps {
  jobId: string;
  candidates: CandidateSummary[];
  counts: CandidateStatusCounts;
  isReadOnly: boolean;
  onStatusChange: (candidateId: string, newStatus: CandidateStatus) => Promise<boolean>;
  onCandidateClick: (candidateId: string) => void;
}

export function KanbanBoard({
  candidates,
  counts,
  isReadOnly,
  onStatusChange,
  onCandidateClick,
}: KanbanBoardProps) {
  const [localCandidates, setLocalCandidates] = useState<CandidateSummary[]>(candidates);
  const [localCounts, setLocalCounts] = useState<CandidateStatusCounts>(counts);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDragSourceStatus, setActiveDragSourceStatus] = useState<CandidateStatus | null>(null);

  // Sync with props when they change
  useMemo(() => {
    setLocalCandidates(candidates);
    setLocalCounts(counts);
  }, [candidates, counts]);

  const columns = useMemo(() => groupCandidatesByStatus(localCandidates), [localCandidates]);
  const validTargets = useMemo(
    () => (activeDragSourceStatus ? getValidTargets(activeDragSourceStatus) : []),
    [activeDragSourceStatus]
  );

  const activeDragCandidate = useMemo(
    () => (activeDragId ? localCandidates.find((c) => c.candidateId === activeDragId) : null),
    [activeDragId, localCandidates]
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const moveCandidate = useCallback(
    async (candidateId: string, newStatus: CandidateStatus) => {
      const candidate = localCandidates.find((c) => c.candidateId === candidateId);
      if (!candidate) return;

      const oldStatus = candidate.status;
      const valid = getValidTargets(oldStatus);
      if (!valid.includes(newStatus)) {
        toast.error(`Transição não permitida. Movimentos válidos: ${valid.join(', ')}`);
        return;
      }

      // Optimistic update
      const prevCandidates = [...localCandidates];
      const prevCounts = { ...localCounts };

      setLocalCandidates((prev) =>
        prev.map((c) => (c.candidateId === candidateId ? { ...c, status: newStatus } : c))
      );
      setLocalCounts((prev) => ({
        ...prev,
        [oldStatus]: (prev[oldStatus] ?? 0) - 1,
        [newStatus]: (prev[newStatus] ?? 0) + 1,
      }));

      const success = await onStatusChange(candidateId, newStatus);
      if (!success) {
        // Rollback
        setLocalCandidates(prevCandidates);
        setLocalCounts(prevCounts);
        toast.error('Erro ao atualizar status do candidato. Tente novamente.');
      }
    },
    [localCandidates, localCounts, onStatusChange]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = event.active.id as string;
    const status = event.active.data.current?.status as CandidateStatus;
    setActiveDragId(id);
    setActiveDragSourceStatus(status);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragId(null);
      setActiveDragSourceStatus(null);

      if (!over) return;
      const candidateId = active.id as string;
      const targetStatus = over.id as CandidateStatus;
      const sourceStatus = active.data.current?.status as CandidateStatus;

      if (sourceStatus === targetStatus) return;
      moveCandidate(candidateId, targetStatus);
    },
    [moveCandidate]
  );

  const handleQuickAdvance = useCallback(
    (candidateId: string) => {
      const candidate = localCandidates.find((c) => c.candidateId === candidateId);
      if (!candidate) return;
      const next = getNextStatus(candidate.status);
      if (!next) {
        toast.error('Não é possível avançar candidato no status atual.');
        return;
      }
      moveCandidate(candidateId, next);
    },
    [localCandidates, moveCandidate]
  );

  const handleQuickReject = useCallback(
    (candidateId: string) => {
      const candidate = localCandidates.find((c) => c.candidateId === candidateId);
      if (!candidate) return;
      if (!canReject(candidate.status)) {
        toast.error('Não é possível rejeitar candidato no status atual.');
        return;
      }
      moveCandidate(candidateId, CandidateStatus.REJECTED);
    },
    [localCandidates, moveCandidate]
  );

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {KANBAN_COLUMNS.map((col) => {
          let isValidTarget: boolean | null = null;
          if (activeDragId) {
            isValidTarget = validTargets.includes(col.status);
          }

          return (
            <KanbanColumn
              key={col.status}
              status={col.status}
              label={col.label}
              color={col.color}
              count={localCounts[col.status] ?? 0}
              candidates={columns[col.status] ?? []}
              isValidTarget={isValidTarget}
              isReadOnly={isReadOnly}
              onCandidateClick={onCandidateClick}
              onQuickReject={handleQuickReject}
              onQuickAdvance={handleQuickAdvance}
            />
          );
        })}
      </div>

      <DragOverlay>
        {activeDragCandidate ? (
          <CandidateCard
            candidate={activeDragCandidate}
            isReadOnly
            isDragging
            onCandidateClick={() => {}}
            onQuickReject={() => {}}
            onQuickAdvance={() => {}}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
